import { useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SEO_META_DATA } from 'apollo/seo';
import { SeoMetadata } from 'types/seoMetadata';
import { log } from 'utils/log';
import { SeoType } from 'types/seoType';
import { RoutePaths } from 'types/enums';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { EventIds } from '../../constants';

const useSeoMetaData = (): {
    loading: boolean;
    seoMetaData?: Partial<SeoType>;
} => {
    const toLocalizedPath = useToLocalizedPath();

    const matchInstruments = useMatch(
        toLocalizedPath(`/${RoutePaths.Instruments}/:domain`),
    );
    const matchGenres = useMatch(
        toLocalizedPath(`/${RoutePaths.Genres}/:domain`),
    );

    const match = matchInstruments || matchGenres;

    const seoUrl = match?.pathname?.startsWith('/')
        ? match?.pathname.slice(1)
        : match?.pathname;

    const { loading, error, data } = useQuery<{
        seoByUrl: SeoMetadata;
        fetchPolicy: 'network-only';
        nextFetchPolicy: 'cache-first';
    }>(SEO_META_DATA, {
        skip: seoUrl === undefined,
        variables: { url: seoUrl },
    });

    const seoMetaData = data?.seoByUrl && {
        title: data?.seoByUrl.title,
        description: data?.seoByUrl.description,
        header: data?.seoByUrl.header,
        byline: data?.seoByUrl.byline,
    };

    useEffect(() => {
        if (error) {
            log.error(
                `Failed to fetch seo meta data by url`,
                EventIds.FailedGetSeoMetaData,
                error,
            );
        }
    }, [error]);

    return { loading, seoMetaData };
};

export default useSeoMetaData;
